import React, { useState, useEffect, useContext } from "react";
import { TutorialContext } from "./TutorialContext";
import QuestContext from "../../../../components/QuestWrapper";
import Header from "./Header";
import Content from "./Content";
import Footer from "./Footer";
import Step from "./Step";
import { TutorialComponents, TutorialModularProps } from "./types";
import General, { useGeneral } from "../../../../general";
import {
  DEFAULT_BRAND_THEME,
  DEFAULT_QUEST_THEME,
  TutorialProps,
  TutorialStep,
} from "../components/tutorial.types";
import TutorialApi from "../components/TutorialApi";
import TutorialThemeManager from "../components/themeManager";
import '../components/tutorial.css'
import Loader from "../../login/components/Loader";


const Tutorial: React.FC<TutorialModularProps> & TutorialComponents = ({
  userId,
  token,
  questId,
  isOpen = true,
  uniqueUserId,
  uniqueEmailId,
  iconColor = "#939393",
  onLinkTrigger = (link) => {
    window.open(link, "smallWindow", "width=500,height=500");
  },
  styleConfig,
  variation,
  children,
  Headers,
  entityAuthenticationToken,
  customLoader,
  onClose,
  ...props
}) => {
  const { apiKey, apiSecret, entityId, apiType, themeConfig, user } = useContext(
    QuestContext.Context
  );

  const [formdata, setFormdata] = useState<TutorialStep[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [campaignVariationId, setCampaignVariationId] = useState("");
  const [questThemeData, setQuestThemeData] = useState(DEFAULT_QUEST_THEME);
  const [BrandTheme, setBrandTheme] = useState(DEFAULT_BRAND_THEME);
  const [HeadersApi, setHeadersApi] = useState(Headers);

  const GeneralFunctions = new General("mixpanel", apiType);
  const tutorialApi = new TutorialApi(apiType);
  const general = useGeneral('quest')
  const [showFooter, setShowFooter] = useState(true);
  const themeManager = new TutorialThemeManager(
    themeConfig,
    styleConfig,
    questThemeData,
    BrandTheme
  );


  const handleNextStep = async (id: string, url: string) => {
    GeneralFunctions.fireTrackingEvent(
      "quest_tutorial_step_link_clicked",
      "tutorial"
    );

    general.trackEvent("quest-tutorial-step-click",
      (uniqueUserId || userId || user.userId)!,
      {},
      true,
      !uniqueUserId
    );

    try {
      setShowLoader(true);
      const userAuth = await tutorialApi.getUserAuth({
        apiKey,
        entityId,
        uniqueUserId,
        uniqueEmailId,
        userId,
        token,
        entityAuthenticationToken
      });

      if (!userAuth) return;

      const headers = {
        apiKey,
        apiSecret,
        userId: userAuth.userId,
        token: userAuth.token,
      };

      await tutorialApi.verifyAction(
        entityId,
        questId,
        headers,
        id,
        campaignVariationId
      );

      onLinkTrigger(url);

      const updatedFormData = formdata.map((item) => {
        if (!item.status && item.id === id) {
          return { ...item, status: true };
        }
        return item;
      });

      setFormdata(updatedFormData);
    } catch (error) {
      GeneralFunctions.captureSentryException(error);
    } finally {
      setShowLoader(false);
    }
  };

  const fetchCampaignData = async (headers: any) => {
    try {
      const response = await tutorialApi.fetchCampaignData(
        entityId,
        questId,
        headers,
        variation
      );

      if (response.sdkConfig?.uiProps?.questThemeData) {
        setQuestThemeData(response.sdkConfig.uiProps.questThemeData);
        if (response.sdkConfig.uiProps.BrandTheme) {
          setBrandTheme(response.sdkConfig.uiProps.BrandTheme.properties);
        }
        if (response?.sdkConfig?.uiProps?.showFooter !== undefined) {
          setShowFooter(response.sdkConfig.uiProps.showFooter);
        }
      }

      if (response.actions) {
        const processedActions = tutorialApi.processActionsData(
          response.actions
        );
        setFormdata(processedActions);
        setCampaignVariationId(response.campaignVariationId);
        if (!Headers || Headers?.length === 0) {
          setHeadersApi(response?.sdkConfig?.uiProps?.Headers);
        }
      }
    } catch (error) {
      GeneralFunctions.captureSentryException(error);
    }
  };

  useEffect(() => {
    GeneralFunctions.fireTrackingEvent("quest_tutorial_loaded", "tutorial");

    general.trackEvent("quest-tutorial-loaded",
      (uniqueUserId || userId || user.userId)!,
      {},
      true,
      !uniqueUserId,
      true
    );

    const initializeCampaign = async () => {
      if (!entityId) return;
      setShowLoader(true);
      try {
        const userAuth = await tutorialApi.getUserAuth({
          apiKey,
          entityId,
          uniqueUserId,
          uniqueEmailId,
          userId,
          token,
          entityAuthenticationToken,
        });

        if (!userAuth) return;

        const headers = {
          apiKey,
          apiSecret,
          userId: userAuth.userId,
          token: userAuth.token,
        };

        await fetchCampaignData(headers);
      } catch (error) {
        GeneralFunctions.captureSentryException(error);
      } finally {
        setShowLoader(false);
      }
    };

    initializeCampaign();
  }, []);

  const handleStepLoad = (index: number, height: number) => {
    const connector = document.querySelector(
      `#q_tutorial_progress_connector_${index}`
    ) as HTMLElement;
    const nextContent = document.querySelector(
      `#q_tutorial_box_content_${index + 1}`
    ) as HTMLElement;

    if (connector && nextContent) {
      let connectorHeight =
        (height - 32) / 2 + (nextContent.offsetHeight - 32) / 2 + 32;
      connector.style.height = `${connectorHeight}px`;

      if (index === formdata.length - 1) {
        connector.style.display = "none";
      }
    }
  };


  if (!isOpen) return null;

  const contextValue = {
    formdata,
    handleNextStep,
    themeManager,
    iconColor,
    showLoader,
    handleStepLoad,
    Headers: HeadersApi ?? [],
    showFooter,
    onClose,
  };

  if (showLoader) return customLoader || <Loader />;

  return (
    <TutorialContext.Provider value={contextValue}>
      <div
        className="q-tutorial-cont"
        style={themeManager.getContainerStyles()}
        {...props}
      >
        {children}
      </div>
    </TutorialContext.Provider>
  );
};

Tutorial.Header = Header;
Tutorial.Content = Content;
Tutorial.Footer = Footer;
Tutorial.Step = Step;

export default Tutorial;
